import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/modules/collector_v2/lib/formatters";
import { formatProfit, profitTone, formatEpochDate, formatPercent } from "@/modules/collector_v2/lib/replayFormatters";

export function ProfileSummary({ profile, showCulture }) {
  if (!profile) return null;
  const tone = profitTone(profile.total_profit_chips ?? profile.total_profit_visual);

  const kpis = [
    {
      label: "Manos jugadas",
      value: formatNumber(profile.hands_played),
      hint: profile.profile_doc?.hands ? `${formatNumber(profile.profile_doc.hands)} en perfil V2` : null,
    },
    {
      label: "Win rate",
      value: formatPercent(profile.win_rate, 2),
      hint: `${formatNumber(profile.winning_hands)} ganadas`,
    },
    {
      label: "Profit (chips)",
      value: formatProfit(profile.total_profit_chips ?? profile.total_profit_visual),
      hint: profile.total_profit_bb != null
        ? `${profile.total_profit_bb > 0 ? "+" : ""}${Number(profile.total_profit_bb).toFixed(1)} BB`
        : null,
      tone,
    },
    {
      label: "Mesas únicas",
      value: formatNumber(profile.tables_count),
      hint: profile.clubs?.length
        ? `${profile.clubs.length} club${profile.clubs.length === 1 ? "" : "s"}`
        : null,
    },
    {
      label: "Coleccionadas",
      value: formatNumber(profile.collected_hands_count),
      hint: "Marcadas como favoritas",
    },
    {
      label: "Última actividad",
      value: formatEpochDate(profile.last_seen_raw),
      hint: profile.first_seen_raw ? `Desde ${formatEpochDate(profile.first_seen_raw)}` : null,
    },
  ];

  const detector = profile.profile_doc;

  return (
    <div className="space-y-6" data-testid="profile-summary">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="p-0">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {kpi.label}
              </p>
              <p
                className={`text-xl font-semibold mt-1 tabular-nums ${
                  kpi.tone === "positive"
                    ? "text-emerald-600"
                    : kpi.tone === "negative"
                    ? "text-rose-600"
                    : ""
                }`}
                data-testid={`profile-kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {kpi.value}
              </p>
              {kpi.hint && (
                <p className="text-[11px] text-muted-foreground mt-1">{kpi.hint}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Show Culture: badge de "exhibicionista" cuando el jugador muestra
          cartas tras ganar sin showdown. Solo se renderiza si tiene muestra
          mínima (n_wins_no_showdown >= 5) y un rate significativo. */}
      {showCulture && showCulture.n_wins_no_showdown >= 5 && (
        <ShowCultureCard data={showCulture} />
      )}

      {profile.all_nicks?.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Otros nicks usados</CardTitle>
            <CardDescription>Nicks adicionales registrados para este UID.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {profile.all_nicks.map((n) => (
              <Badge key={n} variant="secondary" data-testid="profile-nick-badge">
                {n}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {detector && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base">Estadísticas de juego</CardTitle>
                <CardDescription>
                  Calculado sobre {formatNumber(detector.total_hands)} manos
                  {detector.sample_quality && (
                    <span className="ml-2 inline-flex items-center gap-1">
                      ·
                      <span className={
                        detector.sample_quality >= 4 ? "text-emerald-600 font-bold"
                        : detector.sample_quality === 3 ? "text-emerald-500 font-bold"
                        : detector.sample_quality === 2 ? "text-amber-500 font-bold"
                        : "text-slate-300 font-bold"
                      }>
                        {"★".repeat(detector.sample_quality)}{"☆".repeat(4 - detector.sample_quality)}
                      </span>
                      <span className="text-[11px]">
                        {detector.sample_quality === 4 ? "muy confiable"
                          : detector.sample_quality === 3 ? "confiable"
                          : detector.sample_quality === 2 ? "orientativa"
                          : "muestra insuficiente"}
                      </span>
                    </span>
                  )}
                </CardDescription>
              </div>
              {profile.wsd_rate != null && (
                <Badge
                  variant="outline"
                  className="text-[11px] tabular-nums"
                  title={`Won at Showdown: ${profile.showdown_wins}/${profile.showdown_hands}`}
                >
                  WSD {formatPercent(profile.wsd_rate, 1)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DetectorStat
              label="VPIP"
              value={detector.vpip != null ? formatPercent(detector.vpip, 1) : "N/A"}
              hint={detector.bomb_pot_hands > 0
                ? `${detector.non_bomb_hands || 0} manos cash · ${detector.bomb_pot_hands} bomb pots`
                : "Voluntary $ in pot"}
            />
            <DetectorStat
              label="PFR"
              value={detector.pfr != null ? formatPercent(detector.pfr, 1) : "N/A"}
              hint="Pre-flop raise"
            />
            <DetectorStat
              label="Agresividad"
              value={formatPercent(detector.aggression_rate, 1)}
              hint="Bets+raises / decisiones"
            />
            <DetectorStat
              label="Avg BB / mano"
              value={
                detector.avg_profit_bb != null
                  ? `${detector.avg_profit_bb > 0 ? "+" : ""}${Number(detector.avg_profit_bb).toFixed(2)}`
                  : "—"
              }
              hint={`Total: ${detector.total_profit_bb != null ? `${detector.total_profit_bb > 0 ? "+" : ""}${Number(detector.total_profit_bb).toFixed(1)} BB` : "—"}`}
            />
            <DetectorStat
              label="Fold rate"
              value={formatPercent(detector.fold_rate, 1)}
              hint="Pasividad defensiva"
            />
            <DetectorStat
              label="Pasividad"
              value={formatPercent(detector.passive_rate, 1)}
              hint="Calls + checks / total"
            />
            <DetectorStat
              label="Decisiones humanas"
              value={formatNumber(detector.total_decision_actions)}
              hint="Excluye blinds & bomb pots"
            />
            <DetectorStat
              label="Profit samples válidos"
              value={`${formatNumber(detector.n_profit_samples)} / ${formatNumber(detector.total_hands)}`}
              hint={detector.n_profit_excluded > 0 ? `${detector.n_profit_excluded} outliers excluidos` : "100% válidas"}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DetectorStat({ label, value, hint }) {
  return (
    <div data-testid="detector-stat">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className="text-base font-semibold tabular-nums mt-0.5">{value}</p>
      {hint && (
        <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Show Culture: tarjeta que resume el comportamiento de "exhibicionista"
// (jugadores que muestran cartas voluntariamente tras ganar sin llegar al
// showdown). Útil para perfilar tilt y leakage de información.
// ---------------------------------------------------------------------------
const TIER_COPY = {
  addict:       { label: "Adicto al show",        tone: "rose"    },
  frequent:     { label: "Exhibicionista",         tone: "amber"   },
  casual:       { label: "Show ocasional",         tone: "sky"     },
  absent:       { label: "Sin cultura del show",   tone: "muted"   },
  insufficient: { label: "Muestra insuficiente",   tone: "muted"   },
};

function ShowCultureCard({ data }) {
  const rate = data.show_culture_rate ?? 0;
  const tier = data.tier || "insufficient";
  const meta = TIER_COPY[tier] || TIER_COPY.insufficient;
  const ratePct = formatPercent(rate, 1);

  // Mapeo de tono a clases tailwind (evitar template strings dinámicos para
  // que tailwind purge no se pierda las clases).
  const toneClasses = {
    rose:   "bg-rose-100 text-rose-800 border-rose-200",
    amber:  "bg-amber-100 text-amber-800 border-amber-200",
    sky:    "bg-sky-100 text-sky-800 border-sky-200",
    muted:  "bg-muted text-muted-foreground border-transparent",
  };
  const badgeCls = toneClasses[meta.tone] || toneClasses.muted;

  return (
    <Card data-testid="show-culture-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Cultura del show</CardTitle>
            <CardDescription>
              Frecuencia con la que muestra cartas tras ganar sin showdown.
            </CardDescription>
          </div>
          <Badge
            className={`text-xs font-semibold border ${badgeCls}`}
            data-testid="show-culture-tier-badge"
          >
            {meta.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DetectorStat
          label="Show rate"
          value={ratePct}
        />
        <DetectorStat
          label="Shows tras ganar sin SD"
          value={formatNumber(data.n_shows_after_win_no_sd)}
        />
        <DetectorStat
          label="Wins sin showdown"
          value={formatNumber(data.n_wins_no_showdown)}
        />
        <DetectorStat
          label="Shows completos / parciales"
          value={`${formatNumber(data.n_full_shows)} / ${formatNumber(data.n_partial_shows)}`}
        />
      </CardContent>
    </Card>
  );
}
